export type MetricsTabId =
  | 'sagemaker'
  | 'queue'
  | 'scoring'
  | 'database'
  | 'sentry';

export interface MetricsTab {
  id: MetricsTabId;
  label: string;
}

export const METRICS_TABS: MetricsTab[] = [
  { id: 'sagemaker', label: 'Sagemaker Health' },
  { id: 'queue', label: 'Queue Health' },
  { id: 'scoring', label: 'Scoring Health' },
  { id: 'database', label: 'Database Health' },
  { id: 'sentry', label: 'Sentry Error Tracking' },
];

export const METRICS_SESSION_KEY = 'metricsSession';

export type MetricsInterval = '5m' | '30m' | '1h' | '6h' | '24h' | '1w';

export const METRICS_INTERVALS: { id: MetricsInterval; label: string }[] = [
  { id: '5m', label: '5 minutes' },
  { id: '30m', label: '30 minutes' },
  { id: '1h', label: '1 hour' },
  { id: '6h', label: '6 hours' },
  { id: '24h', label: '24 hours' },
  { id: '1w', label: '1 week' },
];

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

export const METRICS_INTERVAL_MS: Record<MetricsInterval, number> = {
  '5m': 5 * MINUTE,
  '30m': 30 * MINUTE,
  '1h': HOUR,
  '6h': 6 * HOUR,
  '24h': DAY,
  '1w': WEEK,
};

const VISIBLE_POINTS: Record<MetricsInterval, number> = {
  '5m': 12,
  '30m': 12,
  '1h': 12,
  '6h': 12,
  '24h': 30,
  // Scoring API allows at most 31 days; 4 weekly buckets stay under that.
  '1w': 4,
};

export const METRICS_INTERVAL_CONFIG: Record<
  MetricsInterval,
  { lookbackMs: number; periodMs: number }
> = {
  '5m': {
    lookbackMs: VISIBLE_POINTS['5m'] * METRICS_INTERVAL_MS['5m'],
    periodMs: METRICS_INTERVAL_MS['5m'],
  },
  '30m': {
    lookbackMs: VISIBLE_POINTS['30m'] * METRICS_INTERVAL_MS['30m'],
    periodMs: METRICS_INTERVAL_MS['30m'],
  },
  '1h': {
    lookbackMs: VISIBLE_POINTS['1h'] * METRICS_INTERVAL_MS['1h'],
    periodMs: METRICS_INTERVAL_MS['1h'],
  },
  '6h': {
    lookbackMs: VISIBLE_POINTS['6h'] * METRICS_INTERVAL_MS['6h'],
    periodMs: METRICS_INTERVAL_MS['6h'],
  },
  '24h': {
    lookbackMs: VISIBLE_POINTS['24h'] * METRICS_INTERVAL_MS['24h'],
    periodMs: METRICS_INTERVAL_MS['24h'],
  },
  '1w': {
    lookbackMs: VISIBLE_POINTS['1w'] * METRICS_INTERVAL_MS['1w'],
    periodMs: METRICS_INTERVAL_MS['1w'],
  },
};

export type MetricsSeriesId = 'error' | 'retries' | 'start' | 'success';

export const METRICS_SERIES: {
  id: MetricsSeriesId;
  name: string;
  color: string;
}[] = [
  { id: 'error', name: 'error-counter', color: '#E85D4C' },
  { id: 'retries', name: 'retries-counter', color: '#E8A317' },
  { id: 'start', name: 'started-counter', color: '#4C9AE8' },
  { id: 'success', name: 'successes-counter', color: '#3DCC7A' },
];

export type ScoringSeriesKey =
  | 'started'
  | 'success'
  | 'errors'
  | 'aborted'
  | 'slow'
  | 'retries'
  | 'degraded'
  | 'droppedCalls';

export const SAGEMAKER_SERIES_COLORS: Record<string, string> = {
  invocations: '#4C9AE8',
  errors4xx: '#E8A317',
  errors5xx: '#E85D4C',
  modelErrors: '#B692F6',
  rate4xx: '#E8A317',
  rate5xx: '#E85D4C',
  modelLatencyAvgMs: '#4C9AE8',
  modelLatencyP99Ms: '#B692F6',
  overheadLatencyAvgMs: '#2DD4BF',
  overheadLatencyMaxMs: '#F79009',
  cpuUtilization: '#4C9AE8',
  memoryUtilization: '#3DCC7A',
  diskUtilization: '#E8A317',
  gpuUtilization: '#EE46BC',
};

export const DB_SERIES_COLORS: Record<string, string> = {
  connections: '#4C9AE8',
  threadsConnected: '#4C9AE8',
  threadsRunning: '#3DCC7A',
  longTransactions: '#E8A317',
  deadlocks: '#E85D4C',
  loginFailures: '#F79009',
  slowQueries: '#E8A317',
  blockedTransactions: '#B692F6',
  rowLockWaits: '#EE46BC',
  lockWaitTimeouts: '#E85D4C',
  queriesPerSec: '#4C9AE8',
  readIops: '#3DCC7A',
  writeIops: '#E8A317',
  readLatencyMs: '#4C9AE8',
  writeLatencyMs: '#E8A317',
  selectLatencyMs: '#3DCC7A',
  dmlLatencyMs: '#B692F6',
  commitLatencyMs: '#EE46BC',
  replicaLagMs: '#E85D4C',
  sqlLatencyMs: '#98A2B3',
  cpuPct: '#4C9AE8',
  connectionsPct: '#E8A317',
  burstBalancePct: '#3DCC7A',
  freeableMemoryMb: '#4C9AE8',
  freeStorageMb: '#3DCC7A',
  volumeUsedMb: '#E8A317',
};

export const QUEUE_SERIES_COLORS: Record<string, string> = {
  messages: '#4C9AE8',
  consumers: '#3DCC7A',
  newMessages: '#E85D4C',
};

export const SENTRY_SERIES_COLORS: Record<string, string> = {
  reported: '#E85D4C',
  errors: '#E85D4C',
  error: '#E85D4C',
  accepted: '#3DCC7A',
  dropped: '#F79009',
  droppedQuota: '#E8A317',
};

export const SCORING_SERIES: {
  key: ScoringSeriesKey;
  name: string;
  color: string;
}[] = [
  { key: 'started', name: 'started-counter', color: '#4C9AE8' },
  { key: 'success', name: 'success-counter', color: '#3DCC7A' },
  { key: 'errors', name: 'error-counter', color: '#E85D4C' },
  { key: 'aborted', name: 'aborted-counter', color: '#B692F6' },
  { key: 'slow', name: 'slow-counter', color: '#F79009' },
  { key: 'retries', name: 'retries-counter', color: '#E8A317' },
  { key: 'degraded', name: 'degraded-counter', color: '#EE46BC' },
  { key: 'droppedCalls', name: 'dropped-counter', color: '#98A2B3' },
];
